/**
 * Multi-line calendar event card for local appointments (service and direct resource bookings).
 */

import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { VideoCameraOutlined } from '@ant-design/icons';
import type { Appointment } from '@/api/mcp-appointments';
import {
  formatCalendarAppointmentTimeRange,
  getCalendarAppointmentClientLabel,
  getCalendarAppointmentHeadline,
  getCalendarAppointmentResourceLabel,
  isServiceCalendarBooking,
  type CalendarEventAudience,
  type TimeGridAppointmentLayout
} from './calendarUtils';
import type { CustomerDisplayInfo } from '../appointmentClientDisplay';
import type { SupportedLanguage } from '@/i18n';

export interface CalendarAppointmentEventContentProps {
  headline: string;
  timeRange: string;
  clientLabel: string;
  allocatedResources: Appointment['allocatedResources'];
  appointment: Appointment;
  layout?: TimeGridAppointmentLayout;
  audience?: CalendarEventAudience;
  /** Fallback chip title for customer audience when resource/service name is missing. */
  appointmentFallbackLabel?: string;
}

export const CalendarAppointmentEventContent: React.FC<CalendarAppointmentEventContentProps> = ({
  headline,
  timeRange,
  clientLabel,
  allocatedResources,
  appointment,
  layout = 'detailed',
  audience = 'staff',
  appointmentFallbackLabel = 'Appointment'
}) => {
  const { t } = useTranslation('manage');
  const isServiceBooking = isServiceCalendarBooking(appointment);
  const resources = allocatedResources ?? [];
  const resourceLabel = getCalendarAppointmentResourceLabel(appointment);

  const primaryLabel =
    audience === 'customer'
      ? isServiceBooking
        ? headline || resourceLabel || appointmentFallbackLabel
        : resourceLabel || headline || appointmentFallbackLabel
      : isServiceBooking
        ? headline
        : clientLabel;

  const fullMeta =
    audience === 'customer'
      ? timeRange
      : isServiceBooking
        ? `${timeRange} · ${clientLabel}`
        : timeRange;
  const tooltipTitle =
    audience === 'customer' || !isServiceBooking
      ? `${primaryLabel}\n${timeRange}`
      : `${primaryLabel}\n${fullMeta}`;
  const isVideo = appointment.format === 'VIDEO';
  const videoIcon = isVideo ? (
    <VideoCameraOutlined className="fc-appointment-card__video-icon" style={{ fontSize: 13 }} />
  ) : null;

  if (layout === 'compact') {
    const compactLine =
      audience === 'customer' || !isServiceBooking
        ? `${primaryLabel} · ${timeRange}`
        : primaryLabel;

    return (
      <div className="fc-appointment-card fc-appointment-card--compact" title={tooltipTitle}>
        <div className="fc-appointment-card__headline">
          {videoIcon}
          <span className="fc-appointment-card__headline-text">{compactLine}</span>
        </div>
      </div>
    );
  }

  const showTags = layout === 'detailed' && isServiceBooking && resources.length > 0;

  return (
    <div
      className={clsx(
        'fc-appointment-card',
        layout === 'standard' && 'fc-appointment-card--standard'
      )}
      title={tooltipTitle}>
      <div className="fc-appointment-card__headline">
        {videoIcon}
        <span className="fc-appointment-card__headline-text">{primaryLabel}</span>
      </div>
      <div className="fc-appointment-card__meta">{fullMeta}</div>
      {showTags ? (
        <div className="fc-appointment-card__tags">
          {resources.map((resource) => (
            <span key={resource.resourceId} className="fc-appointment-card__tag">
              {resource.name}
              {resource.isHidden
                ? ` ${t('appointmentComponents.calendar.appointment.hiddenSuffix')}`
                : ''}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export function buildCalendarAppointmentEventContentProps(
  appointment: Appointment,
  pendingLabel: string,
  language: SupportedLanguage = 'en',
  userDisplayById?: Map<string, CustomerDisplayInfo> | Record<string, CustomerDisplayInfo>,
  options?: {
    audience?: CalendarEventAudience;
    appointmentFallbackLabel?: string;
  }
): CalendarAppointmentEventContentProps {
  return {
    headline: getCalendarAppointmentHeadline(appointment),
    timeRange: formatCalendarAppointmentTimeRange(appointment.startTime, appointment.endTime, language),
    clientLabel: getCalendarAppointmentClientLabel(appointment, pendingLabel, userDisplayById),
    allocatedResources: appointment.allocatedResources,
    appointment,
    audience: options?.audience ?? 'staff',
    appointmentFallbackLabel: options?.appointmentFallbackLabel
  };
}
